import type { Board, GeometryElement } from 'jsxgraph';
import type { InferActionType } from '../../model';
import type { ActionDefinition, ActionResult, TransitionProfile } from '../../model/widget-action.model';
import { type BaseWidget, WidgetEvent, type WidgetEventCallback } from '../../model/widget-base.model';
import type { WidgetConfig, WidgetState } from '../../model/widget-config.model';
import {
  WIDGET_CONTROL_TYPE,
  type WidgetCanvasSelectionControl,
  type WidgetControl,
} from '../../model/widget-control.model';
import { parseParamPath } from '../../util/param-path';
import {
  animateValue,
  type AnimationResult,
  type ParamHandle,
  type ParameterizedElement,
  animateBoundingBox as animateBoundingBoxUtil,
  type BoundingBoxAnimationContext,
  delay,
  resolveTransition,
  type BuiltInActionContext,
  focusRegion,
  transformView,
  patchDisplay,
  highlight,
  resetView,
  updateParam,
} from './actions';
import { freeBoard, initBoard, type BoardInitOptions } from './board';
import { type BoundingBoxTuple, boundingBoxToTuple } from './coordinate-system';
import { createCoordinateSystem as createCoordinateSystemDisplay, type CoordinateSystemConfig } from './elements';
import { WidgetEventEmitter } from './events';
import {
  type BoundCanvasSelections,
  type CanvasSelectionConfig,
  bindCanvasSelections,
} from './interaction/canvas-selection.bind';
import type { JsxgraphThemeService } from './jsxgraph-theme.service';
import type {
  BaseJsxgraphDisplayConfig,
  InteractionFeatureConfig,
  JsxgraphActionType,
  JsxgraphBoardOptions,
  JsxgraphStatic,
} from './jsxgraph.model';
import { DEFAULT_DISPLAY_CONFIG } from './jsxgraph.model';

// Re-export for convenience
export type { BuiltInActionContext };

/**
 * Abstract base class for JSXGraph-based widgets.
 * Handles common functionality like board management, events, animations, and theming.
 *
 * This class builds JSXGraph API options internally by combining:
 * - Feature configs (what to show)
 * - Theme values (how it looks)
 *
 * Implements BuiltInActionContext to allow action functions to receive the widget
 * instance directly instead of a context bag.
 */
export abstract class JsxgraphBaseWidget<Config extends WidgetConfig = WidgetConfig>
  implements BaseWidget<Config>, BuiltInActionContext
{
  constructor(
    protected container: HTMLElement,
    jsxInstance: JsxgraphStatic,
    readonly themeService: JsxgraphThemeService,
  ) {
    this.jsxInstance = jsxInstance;
  }

  abstract id: string;
  abstract type: Config['type'];
  board!: Board;
  protected jsxInstance: JsxgraphStatic;
  readonly objects: Map<string, GeometryElement> = new Map();
  isDestroyed = false;
  private isAnimating = false;
  protected instanceTransitions: Record<string, TransitionProfile> =
    {}; /** Instance-level transition overrides from behavior config */

  /**
   * Registry of elements that support parameterized updates (UPDATE_PARAM action).
   * Subclasses register elements here during init() to enable animated parameter updates.
   */
  readonly parameterizedElements: Map<string, ParameterizedElement> = new Map();

  /** Canvas selection elements bound to the board */
  protected canvasSelections: BoundCanvasSelections | null = null;

  /** Event emitter for widget event pub/sub */
  private readonly eventEmitter = new WidgetEventEmitter();

  // ==============================================================================
  // FIXED AXIS (equal units via ResizeObserver)
  // ==============================================================================

  private fixedAxis?: 'x' | 'y';
  private fixedAxisBbox?: BoundingBoxTuple;
  private resizeObserver?: ResizeObserver;

  /**
   * Adjust a bounding box to enforce equal units (unitX === unitY) for the given fixed axis.
   * The fixed axis keeps its range; the other axis is adjusted based on the container aspect ratio.
   */
  protected adjustBboxForFixedAxis(boundingbox: BoundingBoxTuple, fixedAxis: 'x' | 'y'): BoundingBoxTuple {
    this.fixedAxis = fixedAxis;
    this.fixedAxisBbox = [...boundingbox];

    const rect = this.container.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return boundingbox;
    }

    const [xStart, top, xEnd, bottom] = boundingbox;
    if (fixedAxis === 'x') {
      const xRange = xEnd - xStart;
      const unit = rect.width / xRange;
      const yRange = rect.height / unit;
      const yMid = (top + bottom) / 2;
      return [xStart, yMid + yRange / 2, xEnd, yMid - yRange / 2];
    } else {
      const yRange = top - bottom;
      const unit = rect.height / yRange;
      const xRange = rect.width / unit;
      const xMid = (xStart + xEnd) / 2;
      return [xMid - xRange / 2, top, xMid + xRange / 2, bottom];
    }
  }

  /**
   * Set up a ResizeObserver to maintain equal units when fixedAxis is configured.
   * Call this after initBoard() when a fixedAxis is active.
   */
  protected setupFixedAxisObserver(): void {
    if (!this.fixedAxis) {
      return;
    }
    this.resizeObserver = new ResizeObserver(this.onContainerResize);
    this.resizeObserver.observe(this.container);
  }

  /**
   * Destroy all created elements in reverse order with error handling.
   * Each element's destroy() is called in a try/catch to ensure cleanup continues.
   */
  protected destroyCreatedElements(elements: Map<string, { destroy: () => void }>): void {
    const ids = Array.from(elements.keys()).reverse();
    for (const id of ids) {
      const element = elements.get(id);
      if (element) {
        try {
          element.destroy();
        } catch (error) {
          console.error(`Failed to destroy element '${id}':`, error);
        }
      }
    }
    elements.clear();
  }

  // ==============================================================================
  // LIFECYCLE
  // ==============================================================================

  abstract init(config: Config): void;

  protected abstract getCurrentConfig(): Config | undefined;

  abstract getState(): WidgetState;

  update(config: Partial<Config>): void {
    const currentConfig = this.getCurrentConfig();
    if (!currentConfig) {
      console.error('Cannot update widget: no config available');
      return;
    }

    const newConfig = { ...currentConfig, ...config };
    this.destroy();
    this.init(newConfig);
    this.emit(WidgetEvent.StateChange, this.getState());
  }

  setState(state: WidgetState): void {
    this.destroy();
    this.init(state as unknown as Config);
  }

  destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.fixedAxis = undefined;
    this.fixedAxisBbox = undefined;

    if (this.canvasSelections) {
      this.canvasSelections.destroy();
      this.canvasSelections = null;
    }

    if (this.board && this.jsxInstance) {
      freeBoard(this.board, this.jsxInstance);
    }

    this.objects.clear();
    this.eventEmitter.clear();
    this.parameterizedElements.clear();
    this.isDestroyed = true;

    (this as unknown as { board: Board | null }).board = null;
  }

  // ==============================================================================
  // EVENT SUBSCRIPTION (delegated to WidgetEventEmitter)
  // ==============================================================================

  on(event: string, callback: WidgetEventCallback): void {
    this.eventEmitter.on(event, callback);
  }

  off(event: string, callback: WidgetEventCallback): void {
    this.eventEmitter.off(event, callback);
  }

  protected emit(event: string, data: unknown): void {
    this.eventEmitter.emit(event, data);
  }

  // ==============================================================================
  // ACTION SYSTEM
  // ==============================================================================

  async executeAction(action: ActionDefinition<InferActionType<Config>>): Promise<ActionResult> {
    if (this.isDestroyed) {
      return { success: false, error: 'Widget has been destroyed' };
    }

    const transition = resolveTransition(action.transition, this.instanceTransitions);

    if (transition?.delay && transition.delay > 0) {
      await delay(transition.delay);
    }

    const result = await this.performAction(action.action, action.payload, transition);
    if (result.success) {
      this.emit(WidgetEvent.AnimationComplete, { action: action.action, payload: action.payload });
    }
    return result;
  }

  protected async performAction(
    action: InferActionType<Config>,
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    switch (action as JsxgraphActionType) {
      case 'FOCUS_REGION':
        return focusRegion(this, payload, transition);
      case 'TRANSFORM_VIEW':
        return transformView(this, payload, transition);
      case 'PATCH_DISPLAY':
        return patchDisplay(this, payload);
      case 'HIGHLIGHT':
        return highlight(this, payload, transition);
      case 'RESET_VIEW':
        return resetView(this, boundingBoxToTuple(DEFAULT_DISPLAY_CONFIG.boundingbox!), transition);
      case 'UPDATE_PARAM':
        return updateParam(this, payload, transition);
      default:
        return { success: false, error: `Unknown action '${action}'` };
    }
  }

  // ==============================================================================
  // ANIMATION UTILITIES
  // ==============================================================================

  protected runAnimatedValue(
    startValue: number,
    endValue: number,
    transition: TransitionProfile | undefined,
    onStep: (value: number) => void,
  ): Promise<AnimationResult> {
    return animateValue({
      startValue,
      endValue,
      transition: transition ?? { duration: 0, easing: 'linear', type: 'none' },
      onStep,
      shouldAbort: () => this.isDestroyed || !this.board,
    });
  }

  protected registerParameterizedElement(id: string, element: ParameterizedElement): void {
    this.parameterizedElements.set(id, element);
  }

  /**
   * Resolve a param path to a read/write handle.
   * Default implementation uses element.param dot notation via `parameterizedElements`.
   * Widgets can override to support other path formats (e.g., bare variable names).
   */
  resolveParam(path: string): ParamHandle | null {
    const parsed = parseParamPath(path);
    if (!parsed.root) {
      return null;
    }
    const element = this.parameterizedElements.get(parsed.root);
    if (!element) {
      return null;
    }
    // Join all segments after root to form the full param key.
    // "f.a" → "a", "pt-xlabel.position.y" → "position.y"
    const paramName = parsed.segments
      .slice(1)
      .map((s) => s.value)
      .join('.');
    return {
      get: () => element.getParams()[paramName] ?? 0,
      set: (value: number) => element.setParams({ [paramName]: value }),
    };
  }

  animateBoundingBox(bbox: [number, number, number, number], transition?: TransitionProfile): Promise<void> {
    const ctx: BoundingBoxAnimationContext = {
      board: this.board,
      isAnimating: this.isAnimating,
      isDestroyed: this.isDestroyed,
      setAnimating: (value) => {
        this.isAnimating = value;
      },
    };
    return animateBoundingBoxUtil(ctx, bbox, transition);
  }

  getRegionBoundingBox(_region: string | number): [number, number, number, number] | null {
    return null;
  }

  onParamAnimationStep(): void {
    // Base: no-op. Subclasses override for per-step reactions.
  }

  // ==============================================================================
  // BOARD INITIALIZATION (delegates to board/ module)
  // ==============================================================================

  protected initBoard(
    options: JsxgraphBoardOptions = {},
    interaction?: InteractionFeatureConfig | boolean,
    backgroundColor?: string,
  ): void {
    if (this.board && this.jsxInstance) {
      freeBoard(this.board, this.jsxInstance);
    }

    this.isDestroyed = false;

    // Auto-resolve background color from canvas config if not explicitly passed
    if (!backgroundColor) {
      const config = this.getCurrentConfig();
      const display = config?.display as BaseJsxgraphDisplayConfig | undefined;
      const bgColorToken = display?.canvas?.color ?? 'neutral-50';
      backgroundColor = `var(--${bgColorToken})`;
    }

    const initOptions: BoardInitOptions = {
      container: this.container,
      widgetId: this.id,
      jsxInstance: this.jsxInstance,
      themeService: this.themeService,
      options,
      interaction,
      backgroundColor,
    };

    this.board = initBoard(initOptions);

    // JSXGraph may reset container styles after board init — reapply
    this.container.style.backgroundColor = backgroundColor;

    this.emit(WidgetEvent.Ready, { boardId: this.container.id });
  }

  // ==============================================================================
  // COORDINATE SYSTEM CREATION
  // ==============================================================================

  protected createCoordinateSystem(config?: CoordinateSystemConfig): void {
    if (!this.board) {
      return;
    }

    const { objects } = createCoordinateSystemDisplay({
      board: this.board,
      themeService: this.themeService,
      config,
    });

    objects.forEach((obj, key) => {
      this.objects.set(key, obj);
    });
  }

  // ==============================================================================
  // CANVAS SELECTIONS
  // ==============================================================================

  /**
   * Initialize on-canvas selection controls from behavior config.
   * Call this at the end of subclass init() after board and elements are ready.
   */
  protected initCanvasSelections(controls: WidgetControl[] | undefined): void {
    if (!controls || !this.board) {
      return;
    }

    const canvasSelectionControls = controls.filter(
      (c): c is WidgetCanvasSelectionControl => c.type === WIDGET_CONTROL_TYPE.CanvasSelection,
    );

    if (canvasSelectionControls.length === 0) {
      return;
    }

    const configs: CanvasSelectionConfig[] = canvasSelectionControls.map((c) => ({
      id: c.id,
      x: typeof c.location.x === 'number' ? c.location.x : 0,
      y: typeof c.location.y === 'number' ? c.location.y : 0,
      label: c.label,
      trigger: c.trigger,
      triggerOff: c.triggerOff,
      group: c.group,
      selected: c.selected,
    }));

    this.canvasSelections = bindCanvasSelections({
      board: this.board,
      themeService: this.themeService,
      configs,
      onSelect: async (config, selected) => {
        if (selected) {
          await this.executeAction(config.trigger as ActionDefinition<InferActionType<Config>>);
        } else if (config.triggerOff) {
          await this.executeAction(config.triggerOff as ActionDefinition<InferActionType<Config>>);
        }
      },
    });

    // Track JSXGraph objects in the base objects map
    this.canvasSelections.getAllJsxObjects().forEach((obj, idx) => {
      this.objects.set(`canvas-selection-${idx}`, obj);
    });
  }

  private readonly onContainerResize = (): void => {
    if (!this.fixedAxis || !this.fixedAxisBbox) {
      return;
    }
    const rect = this.container.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return;
    }

    const [xStart, top, xEnd, bottom] = this.fixedAxisBbox;
    let newBbox: BoundingBoxTuple;

    if (this.fixedAxis === 'x') {
      const xRange = xEnd - xStart;
      const unit = rect.width / xRange;
      const yRange = rect.height / unit;
      const yMid = (top + bottom) / 2;
      newBbox = [xStart, yMid + yRange / 2, xEnd, yMid - yRange / 2];
    } else {
      const yRange = top - bottom;
      const unit = rect.height / yRange;
      const xRange = rect.width / unit;
      const xMid = (xStart + xEnd) / 2;
      newBbox = [xMid - xRange / 2, top, xMid + xRange / 2, bottom];
    }

    this.board.setBoundingBox(newBbox, false);
  };

  // Core elements are created through a factory not directly through the base
}
